/* ────────────────────────────────────────────────────────────────
   Kushface — Store Locator
   Initializes Mapbox with the Kushface dark theme, renders stockist
   pins as flame-glyph SVG markers, syncs list ↔ map selection.

   Reads:
     window.__kushfaceLocator.{token, styleUrl}
     <script type="application/json" data-locator-data> stockist array
   ──────────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  // Map-wrap is the skeleton host (position:relative in store-locator.css).
  // `is-loading` shows the .kf-skel overlay; we add it ONLY once we're
  // committed to building the map, and clear it on load OR any failure
  // path so the skeleton never persists over a missing/failed map.
  function mapWrap() {
    return document.querySelector(".kf-locator__map-wrap");
  }
  function startMapLoading() {
    var host = mapWrap();
    if (host) host.classList.add("is-loading");
  }
  function endMapLoading() {
    var host = mapWrap();
    if (host) host.classList.remove("is-loading");
  }

  function init() {
    var cfg = window.__kushfaceLocator || {};
    // Mapbox unavailable / no token: bail before showing a skeleton, and
    // defensively clear it in case it was already set.
    if (!cfg.token || typeof mapboxgl === "undefined") { endMapLoading(); return; }

    var mapEl = document.getElementById("kf-map");
    // No #kf-map (missing-token Liquid branch rendered .kf-locator__missing
    // instead): never cover that message with a skeleton.
    if (!mapEl) { endMapLoading(); return; }

    var dataEl = document.querySelector("[data-locator-data]");
    var stockists = [];
    if (dataEl) {
      try { stockists = JSON.parse(dataEl.textContent); } catch (e) { stockists = []; }
    }

    mapboxgl.accessToken = cfg.token;

    popup = new mapboxgl.Popup({
      // Offset clears the kushface flame icon (~40px tall) so the popup
      // sits above the marker instead of overlapping it.
      offset: 48,
      closeButton: false,
      closeOnClick: false,
      className: "kf-popup"
    });

    // Compute bounds from stockists, fallback to continental US.
    var bounds;
    if (stockists.length > 0) {
      bounds = new mapboxgl.LngLatBounds();
      stockists.forEach(function (s) {
        if (s.lat && s.lng) bounds.extend([s.lng, s.lat]);
      });
    }

    // Committed to building the map — show the skeleton now. Cleared on
    // `load` below, and on `error` so a failed style/tile load can't
    // leave the skeleton stuck over a broken map.
    startMapLoading();

    var map = new mapboxgl.Map({
      container: "kf-map",
      style: cfg.styleUrl || "mapbox://styles/mapbox/dark-v11",
      center: bounds ? bounds.getCenter() : [-98.5, 39.8],
      zoom: 3.5,
      attributionControl: true,
      cooperativeGestures: true
    });

    map.on("error", endMapLoading);

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    // User panning the map dismisses the active tooltip + selection AND
    // clears history — drag = "I want to look somewhere else," so the
    // cycle starts fresh wherever they land. (flyTo from selectStockist
    // is programmatic and does NOT fire dragstart.)
    map.on("dragstart", function () {
      clearSelection();
      hidePopup();
      resetHistory();
    });

    // Clicking the map background (not a marker, not the popup) closes
    // the tooltip + zooms out to frame the next-nearest stockist
    // alongside the one originally selected. Repeated clicks cycle to
    // progressively farther neighbors of the same anchor, skipping any
    // store already displayed this session — so tap-tap-tap walks the
    // user outward through the area without ever repeating a store.
    //
    // Marker clicks call stopPropagation so they don't trigger this.
    // Popups render outside the map canvas, so clicks on them never
    // reach map.on("click").
    map.on("click", function () {
      if (!selected && !cycleAnchor && !popup.isOpen()) return;

      // First click-out from a fresh selection: re-anchor the cycle to
      // it, BUT preserve displayedIds — history carries across selection
      // changes so subsequent click-outs never re-show a store the user
      // already saw earlier in this session.
      if (selected) {
        cycleAnchor = selected;
        rememberDisplayed(selected.id);
      }
      clearSelection();
      hidePopup();

      if (cycleAnchor) {
        var next = nearestOther(cycleAnchor, stockists, displayedIds);
        if (next) {
          rememberDisplayed(next.id);
          var b = new mapboxgl.LngLatBounds();
          b.extend([cycleAnchor.lng, cycleAnchor.lat]);
          b.extend([next.lng, next.lat]);
          map.fitBounds(b, { padding: 120, maxZoom: 11, duration: 600 });
          return;
        }
        // No unseen neighbors left — gentle pull-back. Keep history
        // intact so a subsequent selection still inherits it.
      }
      map.zoomTo(Math.max(map.getZoom() - 1, 3), { duration: 500 });
    });

    map.on("load", function () {
      // Map tiles are ready — reveal the real map (drop the skeleton).
      endMapLoading();

      // Apply Kushface palette overrides on top of the loaded style.
      try {
        var style = map.getStyle();
        (style.layers || []).forEach(function (layer) {
          if (!layer.type) return;
          if (layer.type === "background") {
            map.setPaintProperty(layer.id, "background-color", "#000000");
          } else if (layer["source-layer"] === "water") {
            map.setPaintProperty(layer.id, "fill-color", "#0B0B0B");
          } else if (layer.type === "line" && layer["source-layer"] === "road") {
            map.setPaintProperty(layer.id, "line-color", "#272A2A");
          } else if (layer.type === "symbol") {
            try { map.setPaintProperty(layer.id, "text-color", "#858585"); } catch (e) {}
            try { map.setPaintProperty(layer.id, "text-halo-color", "#000000"); } catch (e) {}
          } else if (layer.type === "fill" && (layer["source-layer"] === "admin" || layer["source-layer"] === "landuse")) {
            try { map.setPaintProperty(layer.id, "fill-color", "#000000"); } catch (e) {}
          }
        });
      } catch (e) { /* style override is best-effort */ }

      if (bounds && stockists.length > 1) {
        map.fitBounds(bounds, { padding: 80, maxZoom: 10, duration: 0 });
      } else if (stockists.length === 1) {
        map.flyTo({ center: [stockists[0].lng, stockists[0].lat], zoom: 11, duration: 0 });
      }

      addMarkers(map, stockists);
    });

    wireListInteractions(map, stockists);
    wireZipSearch(map, stockists);
  }

  function flameSvg() {
    return '<svg viewBox="0 0 32 40" fill="currentColor" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M16 0 C18 4 21 7 21 13 C21 16 19 18 17 19 C19 20 22 22 22 28 C22 34 19 38 16 40 C13 38 10 34 10 28 C10 22 13 20 15 19 C13 18 11 16 11 13 C11 7 14 4 16 0 Z"/>' +
      '</svg>';
  }

  function esc(v) {
    return String(v == null ? "" : v).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function popupHtml(s) {
    var cityState = [s.city, s.state].filter(Boolean).join(", ");
    var loc = [cityState, s.zip].filter(Boolean).join(" ").trim();
    var dir = "https://www.google.com/maps/dir/?api=1&destination=" +
      encodeURIComponent(s.lat + "," + s.lng);
    return '<div class="kf-pop">' +
      '<p class="kf-pop__name">' + esc(s.name) + "</p>" +
      '<a class="kf-pop__addr" target="_blank" rel="noopener" href="' + esc(dir) + '">' +
      esc(s.address) + (loc ? "<br>" + esc(loc) : "") + "</a>" +
      (s.phone ? '<p class="kf-pop__phone">' + esc(s.phone) + "</p>" : "") +
      "</div>";
  }

  // One shared popup instance — guarantees only one tooltip is ever visible.
  var popup = null;
  var markersById = {};
  var selectedId = null;
  var selected = null;
  // Tracks which stockist the popup is currently rendering. Critical on
  // iOS: tapping a link inside the popup makes the touch leave the
  // marker, which fires `mouseleave` → `showPopupFor(selected)`. Without
  // this guard, that would call `popup.setHTML(...)` and destroy the <a>
  // the finger was on mid-tap, so the click never lands on a live node.
  var currentPopupId = null;

  function showPopupFor(map, s) {
    if (!popup) return;
    if (currentPopupId === s.id && popup.isOpen()) return;
    currentPopupId = s.id;
    popup.setLngLat([s.lng, s.lat]).setHTML(popupHtml(s)).addTo(map);
  }
  function hidePopup() {
    if (popup) popup.remove();
    currentPopupId = null;
  }
  function clearSelection() {
    selectedId = null;
    selected = null;
    Object.keys(markersById).forEach(function (id) {
      markersById[id].el.classList.remove("is-active");
    });
    document.querySelectorAll("[data-locator-list] .kf-stockist.is-active")
      .forEach(function (li) { li.classList.remove("is-active"); });
  }

  // Returns the stockist closest to `target` (by squared lat/lng delta —
  // good enough at city scale; haversine is unnecessary here), excluding
  // any ids in `excludeIds`. Used to cycle through neighbors when the
  // user repeatedly taps the map background.
  function nearestOther(target, list, excludeIds) {
    var skip = {};
    (excludeIds || [target.id]).forEach(function (id) { skip[id] = true; });
    var best = null;
    var min = Infinity;
    list.forEach(function (s) {
      if (!s || skip[s.id]) return;
      if (!s.lat || !s.lng) return;
      var dx = s.lng - target.lng;
      var dy = s.lat - target.lat;
      var d = dx * dx + dy * dy;
      if (d < min) { min = d; best = s; }
    });
    return best;
  }

  // Cycle state for click-out neighbor zoom.
  //
  // `cycleAnchor` is the stockist the most recent click-out cycle is
  // pinned to — each tap on empty map reveals the next-nearest neighbor
  // of THAT store, not of the last framed neighbor.
  //
  // `displayedIds` is a rolling history of every stockist that has been
  // anchored or framed since the last map drag. nearestOther() skips
  // these, so an exploration session never re-frames a store the user
  // has already seen — keep tapping and the cycle keeps marching outward
  // through the area.
  //
  // Both are reset only when the user drags the map (a deliberate
  // "I want to look somewhere else" signal). Picking a new stockist
  // re-anchors the cycle but PRESERVES history so the next click-out
  // still skips everything seen so far.
  var cycleAnchor = null;
  var displayedIds = [];
  function resetHistory() { cycleAnchor = null; displayedIds = []; }
  function rememberDisplayed(id) {
    if (id && displayedIds.indexOf(id) === -1) displayedIds.push(id);
  }

  function addMarkers(map, stockists) {
    var markerUrl = (window.__kushfaceLocator || {}).markerUrl;
    stockists.forEach(function (s) {
      if (!s.lat || !s.lng) return;
      var el = document.createElement("div");
      el.className = "kf-marker";
      el.setAttribute("data-id", s.id);
      if (markerUrl) {
        var img = document.createElement("img");
        img.src = markerUrl;
        img.alt = "";
        el.appendChild(img);
      } else {
        el.innerHTML = flameSvg();
      }
      var marker = new mapboxgl.Marker({ element: el, anchor: "bottom" })
        .setLngLat([s.lng, s.lat])
        .addTo(map);
      markersById[s.id] = { marker: marker, el: el };
      el.addEventListener("click", function (e) {
        // Stop the click from bubbling to map.on("click") — without this
        // the map's "click background → close + zoom out" handler would
        // fire immediately after selecting the marker.
        e.stopPropagation();
        selectStockist(map, s);
      });
      el.addEventListener("mouseenter", function () {
        showPopupFor(map, s);
      });
      el.addEventListener("mouseleave", function () {
        // Return to the pinned selection if there is one; otherwise hide.
        if (selected) showPopupFor(map, selected);
        else hidePopup();
      });
    });
  }

  function selectStockist(map, s) {
    selectedId = s.id;
    selected = s;
    // Picking a stockist adds it to the displayed-history (so a later
    // click-out won't re-frame it as a neighbor). The anchor is set on
    // the next click-out — selection alone doesn't change the cycle.
    rememberDisplayed(s.id);
    Object.keys(markersById).forEach(function (id) {
      markersById[id].el.classList.toggle("is-active", id === s.id);
    });
    document.querySelectorAll("[data-locator-list] .kf-stockist").forEach(function (li) {
      li.classList.toggle("is-active", li.getAttribute("data-id") === s.id);
    });
    showPopupFor(map, s);
    map.flyTo({ center: [s.lng, s.lat], zoom: 12, duration: 900 });
  }

  function wireListInteractions(map, stockists) {
    var byId = {};
    stockists.forEach(function (s) { byId[s.id] = s; });
    document.querySelectorAll("[data-locator-list] .kf-stockist").forEach(function (li) {
      li.addEventListener("click", function (e) {
        // Don't intercept the DIRECTIONS link.
        if (e.target.closest(".kf-stockist__cta")) return;
        var id = li.getAttribute("data-id");
        if (byId[id]) selectStockist(map, byId[id]);
      });
    });
  }

  function wireZipSearch(map, stockists) {
    var form = document.querySelector("[data-locator-search]");
    if (!form) return;
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var zip = (form.elements["zip"].value || "").trim();
      if (!zip) return;
      // Filter list to nearest 5 by zip prefix match (cheap heuristic — no geocoding).
      var matches = stockists.filter(function (s) {
        return (s.zip || "").indexOf(zip.slice(0, 3)) === 0;
      });
      if (matches.length > 0) {
        var bounds = new mapboxgl.LngLatBounds();
        matches.forEach(function (s) { bounds.extend([s.lng, s.lat]); });
        map.fitBounds(bounds, { padding: 80, maxZoom: 11, duration: 900 });
        document.querySelectorAll("[data-locator-list] .kf-stockist").forEach(function (li) {
          var match = matches.some(function (m) { return m.id === li.getAttribute("data-id"); });
          li.style.display = match ? "" : "none";
        });
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      // Mapbox script loads with `defer`; wait a tick for it to be ready.
      if (typeof mapboxgl !== "undefined") init();
      else window.addEventListener("load", init, { once: true });
    });
  } else {
    if (typeof mapboxgl !== "undefined") init();
    else window.addEventListener("load", init, { once: true });
  }
})();
